import React, { useState, useEffect } from "react";
import { ShieldAlert, Trash2, XCircle, CheckCircle, RefreshCw, AlertCircle, FileText, User } from "lucide-react";
import api from "../api/axiosInstance";

const DeletionApprovalCenter = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [remarks, setRemarks] = useState({});

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            // Create a fast endpoint select * from deletion_approvals where status = PENDING
            const res = await api.get("get_pending_deletions.php");
            setRequests(res.data.requests || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (approvalId, actionState) => {
        try {
            const res = await api.post("process_deletion_approval.php", {
                approval_id: approvalId,
                action: actionState,
                remarks: remarks[approvalId] || ""
            });
            if (res.data.status === "success") {
                fetchRequests();
            }
        } catch (err) {
            alert(err.response?.data?.message || "Execution error encountered");
        }
    };

    const handleRemarkChange = (id, value) => {
        setRemarks(prev => ({ ...prev, [id]: value }));
    };

    if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-400 uppercase tracking-widest">Scanning Security Registry...</div>;

    return (
        <div className="p-8 space-y-8 bg-slate-50/50 min-h-screen">
            {/* Title block */}
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-slate-900 flex items-center gap-3">
                        <ShieldAlert size={36} className="text-rose-500 animate-pulse" /> Authorization Gate
                    </h1>
                    <p className="text-[10px] font-black uppercase text-rose-500 tracking-widest mt-1">Destructive Action Risk Management Registry</p>
                </div>
                <button onClick={fetchRequests} className="p-3 bg-white border border-slate-100 rounded-2xl shadow-sm hover:rotate-180 transition-all duration-500">
                    <RefreshCw size={20} className="text-slate-400" />
                </button>
            </div>

            {/* Grid Monitor Container */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {requests.map((req) => (
                    <div key={req.id} className="bg-white border-2 border-rose-100/60 rounded-[36px] p-8 shadow-sm flex flex-col justify-between space-y-6 hover:shadow-md transition-all relative overflow-hidden">
                        <div className="absolute top-0 right-0 px-5 py-2 bg-rose-50 text-rose-600 font-black text-[9px] uppercase tracking-widest rounded-bl-2xl border-l border-b border-rose-100/30">
                            {req.target_table.replace(/_/g, ' ')}
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-slate-400">
                                <User size={14} />
                                <p className="text-[10px] font-black uppercase tracking-tight">Initiated By Agent ID: #{req.requested_by}</p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl space-y-1 border border-slate-100">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider block">Target Payload Profile Summary</span>
                                <p className="font-black text-slate-900 uppercase text-sm italic">{req.meta_summary}</p>
                                <p className="text-[10px] font-bold text-slate-400">Database Record Key ID: #{req.target_id}</p>
                            </div>

                            <div>
                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Rejection Reason / Execution Remarks</label>
                                <input
                                    type="text"
                                    placeholder="Provide audit reason context if denying..."
                                    className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold outline-none focus:ring-2 ring-rose-500/20"
                                    value={remarks[req.id] || ""}
                                    onChange={(e) => handleRemarkChange(req.id, e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Twin Action Management Block */}
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-50">
                            <button
                                onClick={() => handleAction(req.id, 'REJECTED')}
                                className="w-full bg-slate-100 hover:bg-slate-900 text-slate-700 hover:text-white py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5"
                            >
                                <XCircle size={14} /> Deny Request
                            </button>
                            <button
                                onClick={() => handleAction(req.id, 'APPROVED')}
                                className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest tracking-wider transition-all flex items-center justify-center gap-1.5 shadow-lg shadow-rose-600/10"
                            >
                                <Trash2 size={14} /> Confirm Purge
                            </button>
                        </div>
                    </div>
                ))}

                {requests.length === 0 && (
                    <div className="col-span-2 text-center bg-white border border-dashed border-slate-200 rounded-[40px] p-20 text-slate-400 text-xs font-black uppercase tracking-widest flex flex-col items-center justify-center gap-2">
                        <CheckCircle className="text-emerald-500" size={32} />
                        Ecosystem Integrity Solidified • No Pending Security Risks Identified
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeletionApprovalCenter;